import { useEffect, useRef, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { uploadChunked, IMAGE_ACCEPT, isImage } from "@/lib/upload";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Camera, Trash2, Loader2, ImageOff } from "lucide-react";
import { toast } from "sonner";

const MAX = 3;

function Thumb({ viewPath, photo, locked, onOpen, onDelete }) {
  const [src, setSrc] = useState(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let url;
    api.get(viewPath, { responseType: "blob" })
      .then((r) => { url = URL.createObjectURL(r.data); setSrc(url); })
      .catch(() => setFailed(true));
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [viewPath]);
  const Icon = failed ? ImageOff : Loader2;
  return (
    <div className="relative group h-16 w-16 rounded-lg overflow-hidden border border-slate-200 bg-slate-100" data-testid={`checklist-photo-${photo.id}`}>
      {src ? <button type="button" onClick={() => onOpen(src)} className="h-full w-full"><img src={src} alt={photo.filename} className="h-full w-full object-cover" /></button>
        : <div className="h-full w-full flex items-center justify-center text-slate-400"><Icon className={`h-4 w-4 ${failed ? "" : "animate-spin"}`} /></div>}
      {!locked && (
        <button type="button" onClick={() => onDelete(photo)} data-testid={`checklist-photo-delete-${photo.id}`}
          className="absolute top-0.5 right-0.5 h-5 w-5 rounded-full bg-black/60 text-white hidden group-hover:flex items-center justify-center hover:bg-rose-600">
          <Trash2 className="h-3 w-3" />
        </button>
      )}
    </div>
  );
}

export const moveInPaths = (checklistId) => ({
  upload: (room, item) => `/tenant/move-in/items/${room}/${item}/photos`,
  view: (pid) => `/move-in/${checklistId}/photos/${pid}`,
  remove: (pid) => `/tenant/move-in/photos/${pid}`,
});

export function ChecklistPhotos({ checklistId, roomKey, item, locked, onChange, paths }) {
  const p = paths || moveInPaths(checklistId);
  const [open, setOpen] = useState(null);
  const [progress, setProgress] = useState(null);
  const ref = useRef(null);
  const photos = item.photos || [];
  const canAdd = !locked && photos.length < MAX;

  const add = async (e) => {
    const files = Array.from(e.target.files || []).filter((f) => isImage(f) && f.size <= 10 * 1024 * 1024).slice(0, MAX - photos.length);
    e.target.value = "";
    if (!files.length) return toast.error("Only JPG, PNG or WEBP photos under 10 MB");
    setProgress(1);
    try {
      for (let i = 0; i < files.length; i++) {
        const meta = await uploadChunked(files[i], (pr) => setProgress(Math.round(((i + pr / 100) / files.length) * 100)));
        await api.post(p.upload(roomKey, item.key), meta);
      }
      toast.success(`${files.length} photo(s) added`);
      onChange?.();
    } catch (err) { toast.error(apiErr(err)); } finally { setProgress(null); }
  };

  const del = async (photo) => {
    try { await api.delete(p.remove(photo.id)); onChange?.(); }
    catch (err) { toast.error(apiErr(err)); }
  };

  if (!photos.length && !canAdd) return null;

  return (
    <div className="flex flex-wrap gap-2 mt-2" data-testid={`checklist-photos-${roomKey}-${item.key}`}>
      {photos.map((ph) => <Thumb key={ph.id} viewPath={p.view(ph.id)} photo={ph} locked={locked} onOpen={setOpen} onDelete={del} />)}
      {canAdd && (
        <button type="button" onClick={() => ref.current?.click()} disabled={progress !== null} data-testid={`checklist-photo-add-${roomKey}-${item.key}`}
          className="h-16 w-16 rounded-lg border-2 border-dashed border-slate-300 text-slate-400 hover:border-primary hover:text-primary flex flex-col items-center justify-center gap-0.5 text-[10px] font-semibold transition-colors disabled:opacity-60">
          {progress !== null ? <><Loader2 className="h-4 w-4 animate-spin" /> {progress}%</> : <><Camera className="h-4 w-4" /> Photo</>}
        </button>
      )}
      <input ref={ref} type="file" accept={IMAGE_ACCEPT} multiple className="hidden" onChange={add} data-testid={`checklist-photo-input-${roomKey}-${item.key}`} />
      <Dialog open={!!open} onOpenChange={(o) => !o && setOpen(null)}>
        <DialogContent className="max-w-3xl p-2 bg-black/95 border-0" data-testid="checklist-lightbox">
          <DialogTitle className="sr-only">Photo</DialogTitle>
          <DialogDescription className="sr-only">Full-size move-in photo</DialogDescription>
          {open && <img src={open} alt="" className="max-h-[80vh] w-full object-contain rounded" />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
